import { BuiltSegment, CaptureChunk, ChunkIndexRow } from "../domain/types";

const mergeBytes = (chunks: Uint8Array[]): Uint8Array => {
  const totalBytes = chunks.reduce((total, chunk) => total + chunk.byteLength, 0);
  const merged = new Uint8Array(totalBytes);
  let offset = 0;

  for (let index = 0; index < chunks.length; index += 1) {
    const chunk = chunks[index];
    merged.set(chunk, offset);
    offset += chunk.byteLength;
  }

  return merged;
};

export class ActiveSegmentBuilder {
  private chunks: CaptureChunk[] = [];
  private totalBytes = 0;
  private totalDurationMs = 0;

  append(chunk: CaptureChunk): void {
    this.chunks.push(chunk);
    this.totalBytes += chunk.bytes.byteLength;
    this.totalDurationMs += chunk.durationMs;
  }

  shouldFlush(
    now: number,
    options: {
      maxBytes: number;
      maxDurationMs: number;
      flushIntervalMs: number;
      lastFlushAt: number | null;
    }
  ): boolean {
    if (this.chunks.length === 0) {
      return false;
    }

    if (this.totalBytes >= options.maxBytes) {
      return true;
    }

    if (this.totalDurationMs >= options.maxDurationMs) {
      return true;
    }

    if (
      options.lastFlushAt !== null &&
      now - options.lastFlushAt >= options.flushIntervalMs
    ) {
      return true;
    }

    return false;
  }

  flush(segmentId: number): BuiltSegment | null {
    if (this.chunks.length === 0) {
      return null;
    }

    const chunkIndexRows: ChunkIndexRow[] = [];
    const byteChunks = this.chunks.map((chunk) => chunk.bytes);
    const firstChunk = this.chunks[0];
    const lastChunk = this.chunks[this.chunks.length - 1];
    let offsetBytes = 0;

    for (let index = 0; index < this.chunks.length; index += 1) {
      const chunk = this.chunks[index];
      chunkIndexRows.push({
        sessionId: "",
        seq: chunk.seq,
        chunkId: chunk.chunkId,
        segmentId,
        startMs: chunk.startMs,
        endMs: chunk.endMs,
        durationMs: chunk.durationMs,
        offsetBytes,
        byteLength: chunk.bytes.byteLength,
      });
      offsetBytes += chunk.bytes.byteLength;
    }

    const built: BuiltSegment = {
      segmentId,
      startSeq: firstChunk.seq,
      endSeq: lastChunk.seq,
      startMs: firstChunk.startMs,
      endMs: lastChunk.endMs,
      durationMs: Math.max(lastChunk.endMs - firstChunk.startMs, this.totalDurationMs),
      chunkCount: this.chunks.length,
      bytes: mergeBytes(byteChunks),
      chunkIndexRows,
    };

    this.chunks = [];
    this.totalBytes = 0;
    this.totalDurationMs = 0;

    return built;
  }

  getPendingBytes(): number {
    return this.totalBytes;
  }

  getPendingDurationMs(): number {
    return this.totalDurationMs;
  }

  isEmpty(): boolean {
    return this.chunks.length === 0;
  }
}
